import { useCallback, useEffect, useState } from 'react';
import { conceptualProblemsApi } from '../api/conceptualProblemsApi';
import type {
  ConceptualLastEdit,
  ConceptualProblemsResponse,
  SaveCategoriesResponse,
} from '../types/conceptualProblems';

/**
 * Loads one conceptual category and its problems (feature 029).
 *
 * <p>Three outcomes are kept apart deliberately, because collapsing any two of them shows an
 * editor the wrong thing:</p>
 * <ul>
 *   <li><b>notFound</b> — the id names no category. A bad address; the page offers a way back.</li>
 *   <li><b>error</b> — the request failed. Retryable; the page offers Retry.</li>
 *   <li><b>an empty items list</b> — a real category that holds nothing yet. Normal.</li>
 * </ul>
 */
export interface UseConceptualCategoryResult {
  data: ConceptualProblemsResponse | null;
  loading: boolean;
  /** A request that failed and is worth retrying. Never set for a 404. */
  error: string | null;
  /** The category id does not exist. A bad address, not a failure. */
  notFound: boolean;
  reload: () => void;
  /**
   * Fold a completed save into the loaded category, in place (feature 029, FR-032).
   *
   * <p>No refetch, deliberately. The page holds the WHOLE category in memory (research D8), so a
   * refetch would throw away 200-odd problems and reload them to change one problem's tag list —
   * and, worse, it would drop the problem off the page entirely when the save re-filed it out of
   * the category being browsed. Merging keeps it on screen with its neighbours intact, which is
   * exactly what FR-032 asks for. Follows `useOvercallingDataEditingSession.reconcileCurrentItem`.
   * </p>
   */
  reconcileSave: (saved: SaveCategoriesResponse) => void;
  /**
   * Mark one problem locked in place, after a save was refused with PROBLEM_LOCKED (FR-048): a
   * Super Editor saved it after this category was loaded.
   */
  markLocked: (serial: number, lastEdit: ConceptualLastEdit | null) => void;
}

export function useConceptualCategory(categoryId: number | null | undefined): UseConceptualCategoryResult {
  const [data, setData] = useState<ConceptualProblemsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  useEffect(() => {
    // `undefined` means the route parameter has not resolved to a category yet — distinct from
    // `null`, which is the uncategorised bucket and is a perfectly good thing to ask for.
    if (categoryId === undefined) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    conceptualProblemsApi
      .getProblems(categoryId)
      .then(response => {
        if (!cancelled) {
          setData(response);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404 || status === 400) {
          // A bad address, not a failure — there is nothing to retry, so no error message.
          setNotFound(true);
          setData(null);
        } else {
          setError('Unable to load these problems. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [categoryId, reloadToken]);

  const reconcileSave = useCallback((saved: SaveCategoriesResponse) => {
    setData((previous) => {
      if (!previous) return previous;

      const items = previous.items.map((item) =>
        item.serial === saved.serial
          ? { ...item, categories: saved.categories, lastEdit: saved.lastEdit, locked: saved.locked }
          : item,
      );

      // The problem stays in `items` even when the save took it out of THIS category. That is
      // deliberate and is the whole of FR-032: an editor who has just re-filed a problem is still
      // reading it, and having it vanish mid-sentence would be a worse answer than a list that is
      // briefly a superset of the category. `total` is corrected from the server's own count, so
      // the "k of N" the editor sees is the category's real size rather than the array length.
      // The Uncategorised bucket is a category being browsed too, and a save that gives the
      // problem a real category takes it out of that bucket. It has no id, so the server sends no
      // count for it — the count below is the only way its total can move.
      const movedOut =
        previous.categoryId === null
          ? saved.categories.length > 0
          : !saved.categories.some((c) => c.id === previous.categoryId);

      // Prefer the server's own number wherever it sent one; fall back to a decrement, which is
      // exact for the bucket because a save can only ever take ONE problem out of it.
      const authoritative =
        previous.categoryId === null
          ? undefined
          : saved.affectedCounts.find((c) => c.id === previous.categoryId);

      return {
        ...previous,
        items,
        total: authoritative?.problemCount ?? (movedOut ? previous.total - 1 : previous.total),
      };
    });
  }, []);

  /**
   * A save came back PROBLEM_LOCKED: a Super Editor locked this problem after the page loaded it
   * (FR-048). Mark it locked in place, naming who did it, so the picker and the masthead stop
   * offering an edit the server will only refuse again.
   */
  const markLocked = useCallback((serial: number, lastEdit: ConceptualLastEdit | null) => {
    setData((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        items: previous.items.map((item) =>
          item.serial === serial
            ? { ...item, locked: true, lastEdit: lastEdit ?? item.lastEdit }
            : item,
        ),
      };
    });
  }, []);

  return { data, loading, error, notFound, reload, reconcileSave, markLocked };
}
